// Global Instructions Rule Applied!
//
// "Is Funding Round" is being turned off — now what happens to the round?
// (owner instruction 2026-09-21).
//
// Unticking used to delete the linked round silently — a Series Seed round
// carrying a $7m raise was destroyed that way on the shared dev database on
// 2026-09-12, and nothing about the sequence was PROD-specific. The two
// outcomes are genuinely different — the deal stops being a funding round
// either way, but the round is a company-level record that may outlive it — so
// the user picks, and Cancel leaves everything alone.
//
// Three actions need a custom footer; the design patterns allow one "unless a
// third action is genuinely needed" (docs/FRONTEND_DESIGN_PATTERNS.md).
import React from 'react'
import { Button, Modal, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { RoundDeleteImpact, RoundDeleteImpactFields } from '../../../state_manager/data_models/round_delete_impact.ts'

const DATE_FORMAT = 'MM/DD/YYYY'

export type UnlinkOrDeleteChoice = 'cancel' | 'unlink' | 'delete'

interface UnlinkOrDeleteRoundModalProps {
  open: boolean
  /** null while the preflight is still loading, or if it failed. */
  impact: RoundDeleteImpact | null
  busy?: boolean
  onChoose: (choice: UnlinkOrDeleteChoice) => void
}

export default function UnlinkOrDeleteRoundModal({
  open,
  impact,
  busy = false,
  onChoose
}: UnlinkOrDeleteRoundModalProps): React.ReactElement {
  const roundName = impact?.[RoundDeleteImpactFields.rounds__name] || 'this round'
  const blocked = !!impact?.[RoundDeleteImpactFields.prog__blocked]

  const instruments = impact?.[RoundDeleteImpactFields.prog__instruments] || 0
  const participations = impact?.[RoundDeleteImpactFields.prog__participations] || 0
  // The deal being unticked is itself one of the linked deals.
  const otherDeals = Math.max((impact?.[RoundDeleteImpactFields.prog__linked_deals] || 0) - 1, 0)

  // What the round is, in the user's terms, so the choice is an informed one.
  // Money is formatted — a raw 15000000 is exactly the figure someone needs to
  // read correctly before deciding to destroy it.
  const currencyCode = impact?.[RoundDeleteImpactFields.prog__currency_code] || undefined
  const money = (value: number): string => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: currencyCode ? 'currency' : 'decimal',
        currency: currencyCode,
        maximumFractionDigits: 0
      }).format(value)
    } catch {
      return String(value)
    }
  }

  const facts: string[] = []
  const push = (label: string, value: unknown): void => {
    if (value !== null && value !== undefined && value !== '') facts.push(`${label}: ${value}`)
  }
  if (impact) {
    push('Funding stage', impact[RoundDeleteImpactFields.rounds__funding_stage__label])
    push('Security type', impact[RoundDeleteImpactFields.rounds__security_type__label])
    const closeDate = impact[RoundDeleteImpactFields.rounds__round_close_date]
    push('Close date', closeDate ? dayjs(closeDate).format(DATE_FORMAT) : null)
    const raising = impact[RoundDeleteImpactFields.rounds__raise_amount]
    push('Raising', typeof raising === 'number' ? money(raising) : null)
    const pre = impact[RoundDeleteImpactFields.rounds__pre_money_valuation]
    push('Pre-money valuation', typeof pre === 'number' ? money(pre) : null)
    const post = impact[RoundDeleteImpactFields.rounds__post_money_valuation]
    push('Post-money valuation', typeof post === 'number' ? money(post) : null)
  }

  const goesWithIt: string[] = []
  if (instruments) goesWithIt.push(`${instruments} security link${instruments === 1 ? '' : 's'}`)
  if (participations) goesWithIt.push(`${participations} co-investor${participations === 1 ? '' : 's'}`)
  // "its 1 security link GOES with it" / "its 2 security links GO with it"
  const goesWithItIsPlural = goesWithIt.length > 1 || instruments > 1 || participations > 1

  const deleteButton = (
    <Button
      danger
      loading={busy}
      disabled={blocked}
      onClick={() => onChoose('delete')}
    >
      Delete round
    </Button>
  )

  return (
    <Modal
      title={`${roundName} — unlink or delete?`}
      open={open}
      onCancel={() => onChoose('cancel')}
      maskClosable={!busy}
      width={560}
      footer={[
        <Button key='cancel' disabled={busy} onClick={() => onChoose('cancel')}>
          Cancel
        </Button>,
        <Button key='unlink' loading={busy} onClick={() => onChoose('unlink')}>
          Unlink only
        </Button>,
        blocked ? (
          <Tooltip
            key='delete'
            title='A cap table version or legal agreement set still references this round'
          >
            <span>{deleteButton}</span>
          </Tooltip>
        ) : (
          <span key='delete'>{deleteButton}</span>
        )
      ]}
    >
      <p className='mb-2'>
        This deal will stop being a funding round either way. Choose what happens to the round
        itself — it belongs to the company, not to this deal.
      </p>

      {facts.length > 0 ? (
        <ul className='pl-5 mb-2'>
          {facts.map((fact) => (
            <li key={fact}>{fact}</li>
          ))}
        </ul>
      ) : null}

      <p className='mb-1'>
        <strong>Unlink only</strong> — the round is kept on the company and on any other deals
        linked to it.
      </p>
      <p className='mb-0'>
        <strong>Delete round</strong> — the round is moved to trash
        {goesWithIt.length
          ? `, and its ${goesWithIt.join(' and ')} ${goesWithItIsPlural ? 'go' : 'goes'} with it`
          : ''}
        {otherDeals > 0
          ? `. ${otherDeals} other deal${otherDeals === 1 ? '' : 's'} will be unlinked too`
          : ''}
        .
      </p>
    </Modal>
  )
}
